"use client";

import * as React from "react";
import * as AccordionPrimitive from "@radix-ui/react-accordion";
import { cn } from "@/lib/utils";
import { PortableText } from "@portabletext/react";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { PortableTextBlock } from "sanity";

export default function FAQList({
  faq,
  className = "",
}: {
  faq: {
    question: string;
    answer: PortableTextBlock[];
  }[];
  className?: string;
}) {
  if (!faq || faq.length === 0) return null;

  return (
    <AccordionPrimitive.Root
      type="single"
      collapsible
      className={cn("relative w-full flex flex-col", className)}
    >
      {faq.map((item, index) => (
        <div key={index} className="flex flex-col">
          <AccordionPrimitive.Item
            value={`item-${index + 1}`}
            className="group p-[2.25rem] sm:p-[4rem] relative prose-h3:m-0 rounded-[1.5rem] data-[state=open]:bg-[#F6F9F7] transition-colors duration-300 ease-in-out"
          >
            <AccordionPrimitive.Header className="flex">
              <AccordionPrimitive.Trigger
                className="group flex flex-1 gap-[2rem] sm:gap-[2.5rem] items-center outline-none py-0"
              >
                <div className="bg-[#103037] rounded-[1rem] relative flex items-center justify-center w-[3.5rem] h-[3.5rem] flex-shrink-0">
                  <div className="w-[1.625rem] sm:w-[1.75rem] h-[0.25rem] flex-shrink-0 rounded-[5px] bg-white" />
                  <div className="absolute h-[1.625rem] sm:h-[1.75rem] group-data-[state=open]:h-0 transition-all duration-300 ease-in-out w-[0.25rem] flex-shrink-0 rounded-[5px] bg-white" />
                </div>
                <h3 className="mb-0 font-geist font-medium text-[#103037] text-start text-[2rem] lg:text-[2.25rem] tracking-normal">
                  {item?.question}
                </h3>
              </AccordionPrimitive.Trigger>
            </AccordionPrimitive.Header>

            <AccordionPrimitive.Content className="overflow-hidden text-sm data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down">
              <div className="pb-0 pl-[5.5rem] sm:pl-[6rem] pt-[1rem] richtext richtext-start prose-headings:!text-[2.25rem] prose-headings:!mt-[2.5rem] prose-headings:!mb-[.5rem] prose-strong:text-black/90 prose-strong:font-medium prose-p:mb-[1.5rem] prose-ul:mb-[1.5rem] prose-ul:pl-[2rem] prose-li:m-0 prose-li:p-0 prose-ul:flex prose-ul:flex-col prose-ul:gap-[.5rem]">
                <PortableText value={item?.answer} components={richTextComponent} />
              </div>
            </AccordionPrimitive.Content>

            <hr
              className={cn(
                "translate-y-[1px] opacity-10 group-data-[state=open]:!opacity-0 transition-opacity duration-300 ease-in-out absolute bottom-0 left-1/2 -translate-x-1/2 border-t [border-image:repeating-linear-gradient(to_right,rgba(0,0,0,1)_0,rgba(0,0,0,1)_10px,transparent_10px,transparent_20px)_1] w-[calc(100%-2rem)]",
                faq.length > index + 1 ? "block" : "hidden",
              )}
            />
          </AccordionPrimitive.Item>
        </div>
      ))}
    </AccordionPrimitive.Root>
  );
}
